/*
 * This file is part of the Det Norske Teatret Nettside 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, {
  forwardRef,
  useState,
  useRef,
  useEffect,
  useCallback,
  type ComponentProps,
  type CSSProperties,
  type RefObject,
  type SyntheticEvent,
} from 'react';
import classnames from 'classnames';
import throttle from 'lodash/throttle';
import Loader from 'components/Loader/Loader';
import './Picture.scss';

const DEFAULT_SOURCES: NonNullable<PictureProps['sources']> = [];
const DEFAULT_IMG: NonNullable<PictureProps['img']> = {};
const DEFAULT_ON_ERROR = () => {};
const DEFAULT_ON_LOAD = () => {};
const DEFAULT_ON_RESIZE = () => {};

export interface PictureProps {
  className?: string | null;
  focalPoint?: { x: number; y: number };
  sources?: ComponentProps<'source'>[];
  img?: ComponentProps<'img'>;
  width?: number | null;
  height?: number | null;
  /** Ref to the root element: <picture>, or <img> when there are no sources. */
  rootRef?: RefObject<HTMLPictureElement | HTMLImageElement | null> | null;
  onLoad?: (event: SyntheticEvent<HTMLImageElement>) => void;
  onError?: () => void;
  onResize?: (image: HTMLImageElement | null) => void;
}

/**
 * This is the Picture component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 */

const Picture = forwardRef<HTMLImageElement, PictureProps>(
  (
    {
      className = null,
      focalPoint,
      sources = DEFAULT_SOURCES,
      img = DEFAULT_IMG,
      width = null,
      height = null,
      rootRef = null,
      onResize = DEFAULT_ON_RESIZE,
      onLoad = DEFAULT_ON_LOAD,
      onError = DEFAULT_ON_ERROR,
    },
    ref
  ) => {
    const imageRef = useRef<HTMLImageElement>(null);
    // Without sources the <img> is the root element, so it fills both refs.
    const setImageRef = useCallback(
      (node: HTMLImageElement | null) => {
        imageRef.current = node;
        if (rootRef) rootRef.current = node;
      },
      [rootRef]
    );
    const [error, setError] = useState(false);
    const [loading, setLoading] = useState(true);

    const resizeListener = throttle(() => {
      onResize(imageRef.current);
    });

    useEffect(() => {
      window.addEventListener('resize', resizeListener);
      return function cleanup() {
        window.removeEventListener('resize', resizeListener);
      };
    });

    useEffect(() => {
      if (ref && imageRef) {
        (ref as RefObject<HTMLImageElement | null>).current = imageRef.current;
      }
    }, []);

    function getStyle(): CSSProperties {
      if (!focalPoint) return {};

      return {
        objectPosition: `${focalPoint.x * 100}% ${focalPoint.y * 100}%`,
      };
    }

    function renderLoader() {
      if (!loading) return null;

      return <Loader />;
    }

    if (!sources.length) {
      return (
        <>
          <img
            className={classnames(className, { loading, error })}
            style={getStyle()}
            ref={setImageRef}
            width={width ?? undefined}
            height={height ?? undefined}
            {...img}
            onError={() => {
              setError(true);
              onError();
            }}
            onLoad={e => {
              setError(false);
              setLoading(false);
              resizeListener();
              onLoad(e);
            }}
          />
          {renderLoader()}
        </>
      );
    }

    return (
      <picture
        ref={rootRef as RefObject<HTMLPictureElement | null> | null}
        className={classnames(className, { loading, error })}
      >
        {sources.map((props, key) => (
          <source key={key} {...props} />
        ))}
        <img
          ref={imageRef}
          style={getStyle()}
          width={width ?? undefined}
          height={height ?? undefined}
          {...img}
          onError={() => {
            setError(true);
            onError();
          }}
          onLoad={e => {
            setError(false);
            setLoading(false);
            resizeListener();
            onLoad(e);
          }}
        />
        {renderLoader()}
      </picture>
    );
  }
);

export default Picture;
